import React from 'react'
import { Link } from 'react-router-dom'
import { Shield, Lock, Eye, FileCheck, CheckCircle, ArrowRight } from 'lucide-react'
import { PublicFooter } from '../components/layout/PublicFooter'
import { PublicNavigation } from '../components/layout/PublicNavigation'
import { Button } from '../components/ui/Button'

export const SecurityPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 relative overflow-hidden transition-colors duration-300">
      <PublicNavigation />

      {/* Hero Section */}
      <section className="py-20 lg:py-32 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#69adba]/10 dark:bg-[#69adba]/20 rounded-full mb-6">
              <Shield className="h-8 w-8 text-[#69adba]" />
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100 sm:text-6xl mb-6">
              Security & Compliance
            </h1>
            <p className="mt-6 text-lg leading-8 text-slate-600 dark:text-slate-400">
              Your data security and privacy are our top priorities. We employ industry-leading security measures 
              to protect your information.
            </p>
          </div>
        </div>
      </section>

      {/* Security Features */}
      <section className="py-20 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Encryption */}
            <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
              <div className="h-12 w-12 rounded-xl bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center mb-4">
                <Lock className="h-6 w-6 text-[#69adba]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                End-to-End Encryption
              </h3>
              <p className="text-slate-600 dark:text-slate-400">
                All data is encrypted in transit (TLS 1.3) and at rest (AES-256) to ensure maximum security.
              </p>
            </div>

            {/* Access Controls */}
            <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
              <div className="h-12 w-12 rounded-xl bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center mb-4">
                <Eye className="h-6 w-6 text-[#69adba]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                Access Controls
              </h3>
              <p className="text-slate-600 dark:text-slate-400">
                Role-based access with multi-factor authentication and regular security audits.
              </p>
            </div>

            {/* Compliance */}
            <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
              <div className="h-12 w-12 rounded-xl bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center mb-4">
                <FileCheck className="h-6 w-6 text-[#69adba]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                SOC 2 Certified
              </h3>
              <p className="text-slate-600 dark:text-slate-400">
                Our infrastructure is SOC 2 Type II certified, ensuring the highest standards of security and compliance.
              </p>
            </div>

            {/* Regular Audits */}
            <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
              <div className="h-12 w-12 rounded-xl bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center mb-4">
                <CheckCircle className="h-6 w-6 text-[#69adba]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                Regular Audits
              </h3>
              <p className="text-slate-600 dark:text-slate-400">
                Third-party security audits and penetration testing ensure continuous protection.
              </p>
            </div>

            {/* Monitoring */}
            <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
              <div className="h-12 w-12 rounded-xl bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center mb-4">
                <Shield className="h-6 w-6 text-[#69adba]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                24/7 Monitoring
              </h3>
              <p className="text-slate-600 dark:text-slate-400">
                Continuous security monitoring and rapid incident response to protect your data.
              </p>
            </div>

            {/* Data Privacy */}
            <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
              <div className="h-12 w-12 rounded-xl bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center mb-4">
                <Lock className="h-6 w-6 text-[#69adba]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                Data Privacy
              </h3>
              <p className="text-slate-600 dark:text-slate-400">
                We never sell your data. Your information is used solely to provide our services.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="py-20 bg-slate-50 dark:bg-slate-800/50 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100 text-center mb-12">
              Compliance & Certifications
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white dark:bg-slate-800 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">SOC 2 Type II</h3>
                <p className="text-slate-600 dark:text-slate-400">
                  Certified for security, availability, processing integrity, confidentiality, and privacy.
                </p>
              </div>
              <div className="bg-white dark:bg-slate-800 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">IRS Compliance</h3>
                <p className="text-slate-600 dark:text-slate-400">
                  Full compliance with IRS regulations for 1031 exchanges and qualified intermediary services.
                </p>
              </div>
              <div className="bg-white dark:bg-slate-800 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">GDPR Ready</h3>
                <p className="text-slate-600 dark:text-slate-400">
                  Compliant with GDPR and other international data protection regulations.
                </p>
              </div>
              <div className="bg-white dark:bg-slate-800 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">KYC/AML</h3>
                <p className="text-slate-600 dark:text-slate-400">
                  Know Your Customer and Anti-Money Laundering compliance for all transactions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#69adba] dark:bg-[#5a9aa6]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl mb-4">
            Trust Your Exchange to Secure Hands
          </h2>
          <p className="mt-4 text-lg text-white/90 mb-8">
            Get started with Open1031 today and experience enterprise-grade security.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              className="bg-white text-[#69adba] hover:bg-slate-50"
              onClick={() => {
                window.dispatchEvent(new CustomEvent('openChat'))
              }}
            >
              Get Started
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Link to="/contact">
              <Button variant="outline" className="border-white text-white hover:bg-white/10">
                Learn More
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  )
}

